import { useParams, useNavigate, useLocation, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FreelancerRating } from "@/types/freelancer-rating";
import type { FreelancerListItem } from "@/types/freelancer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, ArrowLeft, Plus, X, Mail, Phone, MapPin, Clock, Briefcase, Globe2, BadgeCheck, Target, Languages, Pencil, Tag } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { DisponibilidadeCard } from "@/components/freelancers/DisponibilidadeCard";
import { FreelancerDetailsSkeleton } from "@/components/freelancers/FreelancerDetailsSkeleton";
import { FreelancerStatus } from "@/types/freelancer-rating";
import { useQueryClient } from "@tanstack/react-query";
import { usePageNavigation } from "@/hooks/use-page-navigation";
import { FreelancerEditModal } from "@/components/freelancers/FreelancerEditModal";
import { FreelancerMergeWizard } from "@/components/freelancers/FreelancerMergeWizard";
import { FreelancerActionsMenu } from "@/components/freelancers/FreelancerActionsMenu";
import { FreelancerIndicacoesAgencia } from "@/components/freelancers/FreelancerIndicacoesAgencia";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { FreelancerProjetos } from "@/components/freelancers/FreelancerProjetos";
import { FreelancerAvaliacoes } from "@/components/freelancers/FreelancerAvaliacoes";
import { FreelancerLinkedInUploadButton } from "@/components/freelancers/FreelancerLinkedInUploadButton";
import { FreelancerLinkedInHistoryCard } from "@/components/freelancers/FreelancerLinkedInHistoryCard";
import { DetailSection } from "@/components/freelancers/DetailSection";
import { FreelancerLinkedInPerfil } from "@/components/freelancers/painel/FreelancerLinkedInPerfil";
import { FreelancerAutoTags } from "@/components/freelancers/FreelancerAutoTags";
import { FreelancerWhatsAppIA } from "@/components/freelancers/FreelancerWhatsAppIA";
import { FreelancerTimeline } from "@/components/freelancers/timeline/FreelancerTimeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatTimeAgo } from "@/utils/dateUtils";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { externalUrlHref } from "@/utils/external-url";
import {
  ExternalLinkButton,
  LINKEDIN_NOT_FOUND_WARNING,
  PORTFOLIO_INVALID_WARNING,
} from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { reportUserError } from "@/lib/report-error";


export default function FreelancerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const vagaId = searchParams.get("vaga") || searchParams.get("fromVaga");
  const isFromSelection = searchParams.get("returnTo") === "selection";
  const isFromDetails = searchParams.get("fromDetails") === "true";
  const pageParam = searchParams.get("page");
  const vagaTab = searchParams.get("vagaTab");
  const etapaId = searchParams.get("etapaId");
  const { navigateBack } = usePageNavigation({ pageStorageKey: 'freelancers_page' });

  const cleanId = id ? id.split('&')[0] : '';

  const [newTag, setNewTag] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState<string | null>(null);
  const [isEditingObs, setIsEditingObs] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMergeWizardOpen, setIsMergeWizardOpen] = useState(false);
  const { screens } = useUserPermissions();
  const canMergeFreelancers = screens.includes("mesclar_freelancers");

  const handleBack = () => {
    // Se veio da vaga com informações de tab e etapa, voltar para lá
    if (vagaId && vagaTab && etapaId) {
      navigate(`/vagas/${vagaId}?tab=${vagaTab}&etapaId=${etapaId}`);
      return;
    }
    
    // Se veio da vaga mas sem informações de tab/etapa, voltar normalmente
    if (vagaId && vagaTab) {
      navigate(`/vagas/${vagaId}?tab=${vagaTab}`);
      return;
    }
    
    // Comportamento original
    if (pageParam) {
      const baseUrl = '/freelancers';
      const urlParams = new URLSearchParams();
      
      urlParams.set('page', pageParam);
      
      if (vagaId) {
        urlParams.set('vaga', vagaId);
      }
      
      navigate(`${baseUrl}?${urlParams.toString()}`);
    } else {
      navigateBack('/freelancers');
    }
  };

  // PERF: o detalhe busca o freelancer e a avaliação POR ID (2 requests) em vez de
  // carregar a tabela inteira de ~5,5k freelancers só para fazer um .find().
  const invalidateFreelancers = useFreelancersInvalidate();

  // Freelancer por id (sempre) — substitui o .find() sobre a tabela inteira.
  const {
    data: fullFreelancer,
    isLoading: isLoadingFreelancer,
    refetch: refetchFullFreelancer,
  } = useQuery({
    queryKey: ["freelancer", cleanId],
    enabled: !!cleanId,
    staleTime: 30_000,
    queryFn: async (): Promise<FreelancerListItem> => {
      const { data, error } = await supabase
        .from("freelancers")
        .select("*")
        .eq("id", cleanId)
        .single();
      if (error) throw error;
      return data as unknown as FreelancerListItem;
    },
  });
  const freelancer = fullFreelancer ?? null;

  // Última avaliação por id — substitui o Map de ratings da tabela inteira.
  const {
    data: avaliacao,
    isLoading: isLoadingAvaliacao,
    refetch: refetchAvaliacaoQuery,
  } = useQuery({
    queryKey: ["freelancer-avaliacao", cleanId],
    enabled: !!cleanId,
    staleTime: 30_000,
    queryFn: async (): Promise<FreelancerRating | null> => {
      const { data, error } = await supabase
        .from("freelancer_avaliacoes")
        .select("*")
        .eq("freelancer_id", cleanId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as FreelancerRating) ?? null;
    },
  });

  // Função para recarregar dados do freelancer (+ invalida a listagem server-side)
  const refetchFreelancer = async () => {
    await refetchFullFreelancer();
    invalidateFreelancers();
  };

  // Função para recarregar dados da avaliação (+ invalida a listagem server-side)
  const refetchAvaliacao = async () => {
    await refetchAvaliacaoQuery();
    invalidateFreelancers();
  };

  useEffect(() => {
    if (avaliacao) {
      if (avaliacao.estrelas !== undefined && avaliacao.estrelas !== null) {
        setRating(avaliacao.estrelas);
      }

      if (avaliacao.observacoes) {
        setObservacoes(avaliacao.observacoes);
      }
    }
  }, [avaliacao]);

  // /freelancers/:id/editar da versão nova chega aqui com ?editar=1: abre a edição.
  useEffect(() => {
    if (searchParams.get("editar") !== "1" || !fullFreelancer) return;
    setIsEditModalOpen(true);
    setSearchParams((atual) => {
      atual.delete("editar");
      return atual;
    }, { replace: true });
  }, [searchParams, setSearchParams, fullFreelancer]);

  // Registra visualização do perfil (M93). Fire-and-forget; dedup 30min no banco.
  // A contagem aparece na listagem na próxima busca server-side.
  useEffect(() => {
    if (!cleanId) return;
    trackFreelancerView(cleanId, "detalhe");
  }, [cleanId]);

  const {
    data: existingTags
  } = useQuery({
    queryKey: ["all-tags"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("freelancer_avaliacoes").select("tags").not("tags", "is", null);
      if (error) throw error;
      const uniqueTags = new Set<string>();
      data.forEach(item => {
        if (item.tags) {
          item.tags.forEach((tag: string) => uniqueTags.add(tag));
        }
      });
      return Array.from(uniqueTags);
    },
    staleTime: 0
  });

  const handleSaveRating = async (overrides?: { rating?: number | null; observacoes?: string | null }) => {
    const {
      data: user
    } = await supabase.auth.getUser();
    if (!user.user) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar avaliação",
        description: "Usuário não autenticado"
      });
      return;
    }
    const nextRating = overrides && "rating" in overrides ? overrides.rating ?? null : rating;
    const nextObservacoes = overrides && "observacoes" in overrides ? overrides.observacoes : observacoes;
    const validatedRating = nextRating !== null ? Math.min(Math.max(nextRating, 0), 10) : null;

    try {
      const {
        error, data
      } = await supabase.from("freelancer_avaliacoes").upsert({
        id: avaliacao?.id,
        freelancer_id: cleanId,
        avaliador_id: user.user.id,
        estrelas: validatedRating,
        observacoes: nextObservacoes !== null && nextObservacoes !== undefined ? nextObservacoes : avaliacao?.observacoes,
        tags: avaliacao?.tags || [],
        status: avaliacao?.status || 'nao_avaliado'
      }).select().maybeSingle();
      
      if (error) {
        console.error('Erro ao salvar avaliação:', error);
        toast({
          variant: "destructive",
          title: "Erro ao salvar avaliação",
          description: reportUserError(error, { source: "freelancer-details" }).description
        });
        return;
      }
      
      console.log("Avaliação salva com sucesso:", data);
      
      toast({
        title: "Avaliação salva com sucesso"
      });
      
      await refetchAvaliacao();
    } catch (error) {
      console.error("Erro ao salvar avaliação:", error);
      toast({
        variant: "destructive",
        title: "Erro ao salvar avaliação",
        description: "Ocorreu um erro inesperado"
      });
    }
  };

  // Clique inline na estrela: aplica e salva na hora (sem formulário).
  const handleStarClick = (star: number) => {
    setRating(star);
    handleSaveRating({ rating: star });
  };

  // Salva a observação editada inline e sai do modo de edição.
  const handleSaveObservacao = async () => {
    await handleSaveRating({ observacoes: observacoes ?? "" });
    setIsEditingObs(false);
  };

  const handleTagInput = (value: string) => {
    setNewTag(value);
    if (value && existingTags) {
      const suggestions = existingTags.filter(tag => tag.toLowerCase().includes(value.toLowerCase()) && (!avaliacao?.tags || !avaliacao.tags.includes(tag)));
      setSuggestedTags(suggestions);
    } else {
      setSuggestedTags([]);
    }
  };

  const handleSelectSuggestedTag = (tag: string) => {
    setNewTag(tag);
    setSuggestedTags([]);
    handleAddTag(tag);
  };

  const handleAddTag = async (selectedTag?: string) => {
    const tagToAdd = selectedTag || newTag.trim();
    if (!tagToAdd) return;
    const {
      data: user
    } = await supabase.auth.getUser();
    if (!user.user) {
      toast({
        variant: "destructive",
        title: "Erro ao adicionar tag",
        description: "Usuário não autenticado"
      });
      return;
    }
    try {
      if (!avaliacao) {
        const {
          error, data
        } = await supabase.from("freelancer_avaliacoes").insert({
          freelancer_id: cleanId,
          avaliador_id: user.user.id,
          tags: [tagToAdd],
          status: 'nao_avaliado'
        }).select().single();
        
        if (error) {
          toast({
            variant: "destructive",
            title: "Erro ao inserir avaliação",
            description: reportUserError(error, { source: "freelancer-details" }).description
          });
          return;
        }
        
        console.log("Nova avaliação criada com tag:", data);
      } else {
        const newTags = [...(avaliacao.tags || []), tagToAdd];
        const {
          error, data
        } = await supabase.from("freelancer_avaliacoes").update({
          tags: newTags
        }).eq('id', avaliacao.id).select().single();
        
        if (error) {
          toast({
            variant: "destructive",
            title: "Erro ao atualizar avaliação com tag",
            description: reportUserError(error, { source: "freelancer-details" }).description
          });
          return;
        }
        
        console.log("Tag adicionada com sucesso:", data);
      }
      
      setNewTag("");
      setSuggestedTags([]);
      toast({
        title: "Tag adicionada",
        description: `A tag "${tagToAdd}" foi adicionada com sucesso.`
      });
      await refetchAvaliacao();
    } catch (error) {
      console.error("Erro ao adicionar tag:", error);
      toast({
        variant: "destructive",
        title: "Erro ao adicionar tag",
        description: "Ocorreu um erro ao processar sua solicitação."
      });
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    if (!avaliacao) return;
    const {
      data: user
    } = await supabase.auth.getUser();
    if (!user.user) {
      toast({
        variant: "destructive",
        title: "Erro ao remover tag",
        description: "Usuário não autenticado"
      });
      return;
    }
    
    try {
      const newTags = avaliacao.tags?.filter(tag => tag !== tagToRemove) || [];
      const {
        error, data
      } = await supabase.from("freelancer_avaliacoes").update({
        tags: newTags
      }).eq('id', avaliacao.id).select().single();
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao remover tag",
          description: reportUserError(error, { source: "freelancer-details" }).description
        });
        return;
      }
      
      console.log("Tag removida com sucesso:", data);
      toast({
        title: "Tag removida",
        description: `A tag "${tagToRemove}" foi removida com sucesso.`
      });
      
      await refetchAvaliacao();
    } catch (error) {
      console.error("Erro ao remover tag:", error);
      toast({
        variant: "destructive",
        title: "Erro ao remover tag",
        description: "Ocorreu um erro inesperado"
      });
    }
  };

  const queryClient = useQueryClient();


  const handleFreelancerUpdate = () => {
    refetchFreelancer();
  };

  // Enquanto a query por id carrega, mostra o shell (skeleton) em vez de tela
  // branca. Só depois de carregar é que decidimos se o freelancer existe.
  if (isLoadingFreelancer) return <FreelancerDetailsSkeleton />;
  if (!freelancer) return null;

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <Button variant="ghost" className="gap-2" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <div className="flex items-center gap-2">
          <FreelancerLinkedInUploadButton
            freelancerId={freelancer.id}
            freelancerNome={freelancer.nome}
          />
          <FreelancerActionsMenu
          freelancerId={freelancer.id}
          freelancerName={freelancer.nome}
          freelancerEmail={freelancer.email}
          isActive={freelancer.is_active !== false}
          triggerVariant="outline"
          triggerSize="sm"
          onEditClick={() => setIsEditModalOpen(true)}
          onMergeClick={() => setIsMergeWizardOpen(true)}
          onChanged={() => refetchFreelancer()}
          copyData={{
            nome: freelancer.nome,
            pretensao_salarial: freelancer.pretensao_salarial,
            areas: freelancer.areas,
            cidade: freelancer.cidade,
            city: freelancer.city,
            state: freelancer.state,
            portfolio_url: freelancer.portfolio_url,
            linkedin_url: freelancer.linkedin_url,
          }}
        />
        </div>
      </div>
      <FreelancerEditModal
        freelancer={fullFreelancer as any} // eslint-disable-line @typescript-eslint/no-explicit-any
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onUpdate={handleFreelancerUpdate}
      />
      {canMergeFreelancers && (
        <FreelancerMergeWizard
          open={isMergeWizardOpen}
          onOpenChange={setIsMergeWizardOpen}
          currentFreelancer={{
            id: freelancer.id,
            nome: freelancer.nome,
            email: freelancer.email,
          }}
        />
      )}

      <div className="space-y-8 w-full">
          <div className={freelancer.is_active === false ? "opacity-70" : ""}>
            <div className="space-y-6">
              {/* Header principal */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate min-w-0">
                    {freelancer.nome}
                  </h1>
                  <div
                    title={freelancer.quality_seal ? "Freelancer Verificado" : "Não Verificado"}
                    className="inline-flex shrink-0"
                  >
                    <BadgeCheck
                      className={`h-5 w-5 ${
                        freelancer.quality_seal
                          ? "text-blue-500 fill-blue-500 stroke-amber-200"
                          : "text-gray-400 stroke-gray-400"
                      }`}
                    />
                  </div>
                  {freelancer.is_active === false && (
                    <Badge variant="destructive" className="shrink-0">Inativo</Badge>
                  )}
                  {freelancer.pretensao_salarial && (
                    <Badge variant="outline" className="shrink-0 text-xs text-amber-600 border-amber-500">
                      {freelancer.pretensao_salarial}
                    </Badge>
                  )}
                  {freelancer.linkedin_nao_encontrado_em && (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-xs text-amber-600 border-amber-500"
                      title={LINKEDIN_NOT_FOUND_WARNING}
                    >
                      LinkedIn não encontrado
                    </Badge>
                  )}
                  {freelancer.portfolio_invalido_em && (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-xs text-amber-600 border-amber-500"
                      title={PORTFOLIO_INVALID_WARNING}
                    >
                      Portfólio inválido
                    </Badge>
                  )}
                  <div className="ml-auto flex items-center gap-2 shrink-0">
                    <ExternalLinkButton
                      kind="portfolio"
                      url={freelancer.portfolio_url}
                      notFoundAt={freelancer.portfolio_invalido_em}
                      variant="inline"
                      icon={Globe2}
                      iconClassName="h-4 w-4"
                    />
                    <FreelancerLinkedInButton
                      freelancerId={freelancer.id}
                      url={freelancer.linkedin_url}
                      variant="inline"
                      iconClassName="h-4 w-4"
                      notFoundAt={freelancer.linkedin_nao_encontrado_em}
                    />
                  </div>
                </div>
                
                {/* Contatos + datas, compactos numa linha */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {freelancer.email}
                  </span>
                  {freelancer.telefone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      {freelancer.telefone}
                    </span>
                  )}
                  {freelancer.cidade && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {freelancer.cidade}
                    </span>
                  )}
                  {freelancer.created_at && (
                    <span>Cadastro {new Date(freelancer.created_at).toLocaleDateString("pt-BR")}</span>
                  )}
                  {!freelancer.unsubscribed_at && freelancer.last_submitted_at && (() => {
                    const timeAgo = formatTimeAgo(freelancer.last_submitted_at);
                    return (
                      <span className={timeAgo.isRecent ? "text-green-600" : "text-amber-600"}>
                        Atualizado {new Date(freelancer.last_submitted_at).toLocaleDateString("pt-BR")} ({timeAgo.text})
                      </span>
                    );
                  })()}
                  {!freelancer.last_submitted_at && (
                    <span className="text-amber-600" title="Não preencheu o formulário de cadastro; o registro guarda o histórico de projetos">
                      Sem cadastro
                    </span>
                  )}
                  {freelancer.unsubscribed_at && (
                    <span className="text-red-600">
                      Descadastro {new Date(freelancer.unsubscribed_at).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </div>
              </div>

              {/* Áreas de atuação | Avaliação | Tags — 3 colunas alinhadas na largura */}
              <div className="grid gap-8 border-t pt-5 sm:grid-cols-3">
                {/* Áreas de atuação */}
                <div className="space-y-2">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Áreas de atuação
                  </h3>
                  {freelancer.areas && freelancer.areas.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {freelancer.areas.map((area) => (
                        <Badge key={area} variant="outline" className="text-xs">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhuma área cadastrada.</p>
                  )}
                </div>

                {/* Avaliação (estrelas + observação, editável inline) */}
                <div className="space-y-1.5 sm:border-l sm:pl-8">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <Star className="h-3.5 w-3.5" /> Avaliação
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {rating !== null ? `${rating}/10` : "sem nota"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-0.5">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleStarClick(star)}
                        className="focus:outline-hidden"
                        title={`${star}/10`}
                      >
                        <Star
                          className={`h-5 w-5 ${
                            star <= (rating ?? 0)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300 hover:text-yellow-200"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {/* Observação — truncada, clique para editar inline */}
                  {isEditingObs ? (
                    <div className="space-y-2">
                      <Textarea
                        autoFocus
                        placeholder="Observações sobre o freelancer"
                        value={observacoes ?? ""}
                        onChange={(e) => setObservacoes(e.target.value)}
                        className="min-h-[80px] text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveObservacao}>
                          Salvar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setObservacoes(avaliacao?.observacoes ?? null);
                            setIsEditingObs(false);
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditingObs(true)}
                      className="group flex w-full items-start gap-1.5 rounded-md px-1.5 py-1 text-left text-sm hover:bg-accent"
                    >
                      {observacoes ? (
                        <span className="line-clamp-2 text-muted-foreground">{observacoes}</span>
                      ) : (
                        <span className="italic text-muted-foreground/70">Adicionar observação...</span>
                      )}
                      <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  )}
                </div>

                {/* Tags */}
                <div className="space-y-1.5 sm:border-l sm:pl-8">
                  <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <Tag className="h-3.5 w-3.5" /> Tags
                  </h3>
                  <div className="relative">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {avaliacao?.tags?.map((tag) => (
                        <Badge key={tag} variant="secondary" className="gap-1">
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-destructive"
                            aria-label={`Remover tag ${tag}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      <div className="flex items-center gap-1">
                        <Input
                          placeholder="Adicionar tag"
                          value={newTag}
                          onChange={(e) => handleTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddTag();
                            }
                          }}
                          className="h-7 w-28 text-xs"
                        />
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            handleAddTag();
                          }}
                          size="icon"
                          type="button"
                          variant="ghost"
                          className="h-7 w-7"
                          aria-label="Adicionar tag"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {suggestedTags.length > 0 && (
                      <Card className="absolute z-10 mt-1 w-48">
                        <div className="space-y-1 p-2">
                          {suggestedTags.map((tag) => (
                            <div
                              key={tag}
                              className="cursor-pointer rounded px-2 py-1 text-sm hover:bg-accent"
                              onClick={() => handleSelectSuggestedTag(tag)}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags automáticas (IA) — somente leitura, distintas das manuais acima */}
              <FreelancerAutoTags freelancerId={freelancer.id} />
            </div>
          </div>

          {/* Abas: visão geral (seções existentes) e linha do tempo (lazy: a
              timeline só monta quando a aba abre, padrão Radix Tabs). */}
          <Tabs
            value={searchParams.get("aba") === "linha-do-tempo" ? "linha-do-tempo" : "visao-geral"}
            onValueChange={(aba) =>
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  if (aba === "visao-geral") next.delete("aba");
                  else next.set("aba", aba);
                  return next;
                },
                { replace: true },
              )
            }
            className="w-full"
          >
            <TabsList>
              <TabsTrigger value="visao-geral">Visão geral</TabsTrigger>
              <TabsTrigger value="linha-do-tempo">Linha do tempo</TabsTrigger>
            </TabsList>

            <TabsContent value="visao-geral" className="mt-6 space-y-8">

          {/* Experiência profissional (antes de disponibilidade) — inclui objetivos, idiomas e experiências */}
          {(freelancer.tempo_experiencia ||
            freelancer.tipo_atuacao ||
            freelancer.historia_profissional ||
            freelancer.experiencias_anteriores ||
            freelancer.objetivos ||
            (freelancer.idiomas && freelancer.idiomas.length > 0)) && (
            <DetailSection
              title="Experiência Profissional"
              icon={Briefcase}
              action={
                (freelancer.tempo_experiencia || freelancer.tipo_atuacao) && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {freelancer.tempo_experiencia && (
                      <Badge variant="secondary" className="gap-1 font-normal">
                        <Clock className="h-3 w-3" />
                        {freelancer.tempo_experiencia}
                      </Badge>
                    )}
                    {freelancer.tipo_atuacao && (
                      <Badge variant="secondary" className="gap-1 font-normal">
                        <Briefcase className="h-3 w-3" />
                        {freelancer.tipo_atuacao}
                      </Badge>
                    )}
                  </div>
                )
              }
            >
              <div className="grid gap-5">
                {freelancer.historia_profissional && (
                  <div
                    className="text-muted-foreground prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(freelancer.historia_profissional) }}
                  />
                )}

                {freelancer.objetivos && (
                  <div className="space-y-1.5">
                    <h3 className="flex items-center gap-1.5 font-medium">
                      <Target className="h-4 w-4 text-muted-foreground" /> Objetivos
                    </h3>
                    <div
                      className="text-muted-foreground prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(freelancer.objetivos) }}
                    />
                  </div>
                )}

                {freelancer.experiencias_anteriores && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium">Experiências anteriores:</span>
                    {freelancer.experiencias_anteriores
                      .split(/\s+[-–]\s+/)
                      .map((item) => item.trim())
                      .filter(Boolean)
                      .map((exp, index) => (
                        <Badge key={`${exp}-${index}`} variant="outline" className="font-normal">
                          {exp}
                        </Badge>
                      ))}
                  </div>
                )}

                {freelancer.idiomas && freelancer.idiomas.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Languages className="h-4 w-4 text-muted-foreground" /> Idiomas:
                    </span>
                    {freelancer.idiomas.map((idioma) => (
                      <Badge key={idioma} variant="outline" className="font-normal">
                        {idioma}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </DetailSection>
          )}

          <FreelancerLinkedInPerfil freelancerId={freelancer.id} />

          {/* Histórico de LinkedIn (background profissional) */}
          <FreelancerLinkedInHistoryCard freelancerId={freelancer.id} />

          {/* Disponibilidade */}
          <DisponibilidadeCard freelancerId={cleanId} freelancerNome={freelancer.nome} />

          {/* O que a IA leu no WhatsApp (some quando não há nada) */}
          <FreelancerWhatsAppIA freelancerId={cleanId} />

          {/* Projetos (com match de agências na coluna direita) */}
          <FreelancerProjetos
            freelancerId={cleanId}
            sidebar={
              <FreelancerIndicacoesAgencia
                freelancerId={freelancer.id}
                freelancerName={freelancer.nome}
                asSection={false}
              />
            }
          />

          {/* Avaliações */}
          <FreelancerAvaliacoes freelancerId={cleanId} />

          {/* Portfólio */}
          {freelancer.portfolio_url && (
            <DetailSection title="Portfólio" icon={Globe2}>
              <div className="w-full h-[600px]">
                <iframe
                  src={externalUrlHref(freelancer.portfolio_url) ?? undefined}
                  className="w-full h-full rounded-md border border-input"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </DetailSection>
          )}

            </TabsContent>

            <TabsContent value="linha-do-tempo" className="mt-6">
              <FreelancerTimeline freelancerId={cleanId} />
            </TabsContent>
          </Tabs>
      </div>
    </div>
  );
}
